import { mat3 } from "gl-matrix";
import { CardBoundingBoxType, VideoSizeType } from "../types";
import cv from "@techstark/opencv-js";

const drawAllUniqueLines = false;
const drawEdgesAndCorners = false;

// Japanese business cards are 91mm x 55mm
const cardMajorAxis = 91;
const cardMinorAxis = 55;

type Line = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  theta: number;
  r: number;
  unique: boolean;
};

export const CalculateBoundingBox = (videoSize: VideoSizeType, isHorizontalCard: boolean): CardBoundingBoxType | null => {
  if(!videoSize) return null;

  let cardWidth;
  let cardWidthPercent;
  let cardHeight;
  let cardHeightPercent;
  let cardX;
  let cardXPercent;
  let cardY;
  let cardYPercent;

  const cardMargin = 0.1;
  const cardFill = 1 - cardMargin * 2;

  if(isHorizontalCard) {
    if(videoSize.width > videoSize.height * cardMajorAxis/cardMinorAxis) { // very wide video
      cardWidth = videoSize.height * cardFill * cardMajorAxis/cardMinorAxis;
      cardWidthPercent = cardWidth / videoSize.width * 100;
      cardHeight = videoSize.height * cardFill;
      cardHeightPercent = cardHeight / videoSize.height * 100;
      cardX = (videoSize.width - cardWidth)/2;
      cardXPercent = cardX / videoSize.width * 100;
      cardY = videoSize.height * cardMargin;
      cardYPercent = cardY / videoSize.height * 100;
    } else { // kinda tall video
      cardWidth = videoSize.width * cardFill;
      cardWidthPercent = cardWidth / videoSize.width * 100;
      cardHeight = videoSize.width * cardFill * cardMinorAxis/cardMajorAxis;
      cardHeightPercent = cardHeight / videoSize.height * 100;
      cardX = videoSize.width * cardMargin;
      cardXPercent = cardX / videoSize.width * 100;
      cardY = (videoSize.height - cardHeight)/2;
      cardYPercent = cardY / videoSize.height * 100;
    }
  } else { // vertical card
    if(videoSize.height > videoSize.width * cardMajorAxis/cardMinorAxis) { // very tall video
      cardWidth = videoSize.width * cardFill;
      cardWidthPercent = cardWidth / videoSize.width * 100;
      cardHeight = videoSize.width * cardFill * cardMajorAxis/cardMinorAxis;
      cardHeightPercent = cardHeight / videoSize.height * 100;
      cardX = videoSize.width * cardMargin;
      cardXPercent = cardX / videoSize.width * 100;
      cardY = (videoSize.height - cardHeight)/2;
      cardYPercent = cardY / videoSize.height * 100;
    } else { // kinda video
      cardWidth = videoSize.height * cardFill * cardMinorAxis/cardMajorAxis;
      cardWidthPercent = cardWidth / videoSize.width * 100;
      cardHeight = videoSize.height * cardFill;
      cardHeightPercent = cardHeight / videoSize.height * 100;
      cardX = (videoSize.width - cardWidth)/2;
      cardXPercent = cardX / videoSize.width * 100;
      cardY = videoSize.height * cardMargin;
      cardYPercent = cardY / videoSize.height * 100;
    }
  }

  return {
    cardWidth,
    cardWidthPercent,
    cardHeight,
    cardHeightPercent,
    cardX,
    cardXPercent,
    cardY,
    cardYPercent,
    cardMajorAxis,
    cardMinorAxis,
  };
}

export const CropAndCopyCanvas = (video: HTMLVideoElement, outputCanvas: HTMLCanvasElement, isHorizontalCard: boolean, cardBoundingBox: CardBoundingBoxType): void => {
  const outputCtx = outputCanvas?.getContext("2d");

  if (!outputCtx) {
    console.error("Failed to get output canvas context to copy video frame to canvas");
    return;
  }

  // Define desired output dimensions
  const outputWidth = isHorizontalCard ? 853 : 512;
  const outputHeight = isHorizontalCard ? 512 : 853;

  // Resize and clear the output canvas
  outputCanvas.width = outputWidth;
  outputCanvas.height = outputHeight;
  outputCtx.clearRect(0, 0, outputCanvas.width, outputCanvas.height);

  if (video && cardBoundingBox) {
    // Draw the video frame onto the canvas
    outputCtx.drawImage(video, cardBoundingBox.cardX, cardBoundingBox.cardY, cardBoundingBox.cardWidth, cardBoundingBox.cardHeight, 0, 0, outputCanvas.width, outputCanvas.height);
    const originalImageData = outputCtx.getImageData(0, 0, outputCanvas.width, outputCanvas.height);

    // Get image data from the canvas
    let imageData = outputCtx.getImageData(0, 0, outputCanvas.width, outputCanvas.height);
    let data = imageData.data;

    // Convert to grayscale
    for (let i = 0; i < data.length; i += 4) {
      const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
      data[i] = avg; // Red
      data[i + 1] = avg; // Green
      data[i + 2] = avg; // Blue
    }
    outputCtx.putImageData(imageData, 0, 0);

    // Apply blur filter
    outputCtx.filter = "blur(2px)";
    outputCtx.drawImage(outputCanvas, 0, 0);
    outputCtx.filter = "none";

    outputCtx.putImageData(originalImageData, 0, 0);
  }
};

export const DeskewCanvas = (outputCanvas: HTMLCanvasElement): void => {
  const outputCtx = outputCanvas?.getContext("2d");

  if (!outputCtx) {
    console.error("Failed to get output canvas context to paint lines");
    return;
  }

  const isHorizontalCard = outputCanvas.width > outputCanvas.height;

  // Define desired output dimensions
  const outputWidth = isHorizontalCard ? 853 : 512;
  const outputHeight = isHorizontalCard ? 512 : 853;

  const originalImageData = outputCtx.getImageData(0, 0, outputCanvas.width, outputCanvas.height);
  let imageData = outputCtx.getImageData(0, 0, outputCanvas.width, outputCanvas.height);
  let data = imageData.data;

  // Create a temporary canvas to hold the current state
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = outputWidth;
  tempCanvas.height = outputHeight;
  const tempCtx = tempCanvas.getContext("2d");
  if (!tempCtx) {
    console.error("Failed to get temp canvas context");
    return;
  }
  tempCtx.drawImage(outputCanvas, 0, 0);

  // Now, clip to the center region, set a heavier blur filter,
  // and draw from the temporary canvas.
  for (let blurLevel = 0; blurLevel < 10; blurLevel++) {
    const centerX = outputWidth * (0.20 + blurLevel/100);
    const centerY = outputHeight * (0.20 + blurLevel/100);
    const centerWidth = outputWidth * (0.6 - blurLevel/50);
    const centerHeight = outputHeight * (0.6 - blurLevel/50);

    outputCtx.save();
    outputCtx.beginPath();
    outputCtx.rect(centerX, centerY, centerWidth, centerHeight);
    outputCtx.clip();

    // Apply an extra heavy blur (10px) on this clipped area.
    outputCtx.filter = `blur(${blurLevel}px)`;
    outputCtx.drawImage(tempCanvas, 0, 0);
    outputCtx.restore();
    outputCtx.filter = "none";
  }
      
  // Detect edges using simple Sobel-like filter
  imageData = outputCtx.getImageData(0, 0, outputCanvas.width, outputCanvas.height);
  data = imageData.data;
  let edgeData = new Uint8ClampedArray(data.length);
  
  const width = outputCanvas.width;
  const height = outputCanvas.height;
  
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      let i = (y * width + x) * 4;
      let gx = (-data[i - 4] - 2 * data[i - width * 4] - data[i - width * 4 - 4]) + (data[i + 4] + 2 * data[i + width * 4] + data[i + width * 4 + 4]);
      let gy = (-data[i - width * 4 - 4] - 2 * data[i - width * 4] - data[i - 4]) + (data[i + width * 4 + 4] + 2 * data[i + width * 4] + data[i + 4]);
      let edge = Math.sqrt(gx * gx + gy * gy);
      edgeData[i] = edgeData[i + 1] = edgeData[i + 2] = edge;
      edgeData[i + 3] = 255;
    }
  }

  // draw the edge data image
  //outputCtx.putImageData(new ImageData(edgeData, width, height), 0, 0);

  // ----- Hough Transform for Straight Line Detection -----
  // 1. Collect edge points using a threshold
  const edgeThreshold = 30;
  const edgePoints: { x: number; y: number, strength: number }[] = [];
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const idx = (y * width + x) * 4;
      if (edgeData[idx] > edgeThreshold) {
        edgePoints.push({ x, y, strength: edgeData[idx] });
      }
    }
  }
  
  // 2. Create the Hough accumulator
  const thetaSteps = 180;
  const thetaStep = Math.PI / thetaSteps;
  const rMax = Math.ceil(Math.sqrt(width * width + height * height));
  const accumulator1D = new Int32Array(thetaSteps * (2 * rMax + 1));

  // 3. Populate the accumulator with votes from each edge point.
  for (const point of edgePoints) {
    for (let t = 0; t < thetaSteps; t++) {
      const theta = t * thetaStep;
      const r = point.x * Math.cos(theta) + point.y * Math.sin(theta);
      const rIdx = Math.round(r) + rMax; // shift r to be positive index
      const idx = t * (2 * rMax + 1) + rIdx;

      accumulator1D[idx] += point.strength;
    }
  }

  const accumulator: number[][] = Array.from({ length: thetaSteps }, () =>
    new Array(2 * rMax + 1).fill(0)
  );

  for (let t = 0; t < thetaSteps; t++) {
    for (let rIdx = 0; rIdx < 2 * rMax + 1; rIdx++) {
      const idx = t * (2 * rMax + 1) + rIdx;
      accumulator[t][rIdx] = accumulator1D[idx];
    }
  }
  
  // 4. Find peaks in the accumulator.
  type Peak = { thetaIndex: number; rIndex: number; votes: number };
  const peaks: Peak[] = [];
  for (let t = 0; t < thetaSteps; t++) {
    for (let rIdx = 0; rIdx < accumulator[t].length; rIdx++) {
      if (accumulator[t][rIdx] > 0) {
        peaks.push({ thetaIndex: t, rIndex: rIdx, votes: accumulator[t][rIdx] });
      }
    }
  }
  // Sort peaks by votes (highest first) and select top
  peaks.sort((a, b) => b.votes - a.votes);
  const topPeaks = peaks.slice(0, 1500);

  // 5. Find the best-matching horizontal line
  const maxScore = topPeaks[0].votes;
  const targetTheta = 0; // horizontal line
  const targetR = rMax * 0.05; // 5% from the top

  const topEdgePoints = peaks.map(peak => {
    const theta = peak.thetaIndex * thetaStep;
    const r = peak.rIndex - rMax;
    const thetaDiff = Math.min(Math.abs(theta - targetTheta), Math.abs(theta - targetTheta + Math.PI), Math.abs(theta - targetTheta - Math.PI));
    let rDiff = Infinity;
    if (theta < Math.PI / 2 && r > 0) {
      rDiff = Math.abs((r / rMax) - targetR) * 100;
    } else if (theta > Math.PI / 2 && r < 0) {
      rDiff = Math.abs((r / rMax) - targetR) * 100;
    }
    const strengthPenalty = (maxScore - peak.votes) * 25;
    const similarity = thetaDiff * 30 + rDiff + strengthPenalty;
    return { ...peak, similarity };
  })
  .sort((a, b) => a.similarity - b.similarity)
  .slice(0, 3);

  // Define vertical threshold in radians
  const verticalThreshold = 0.5;
  const leftMinR =    width * 0.0;   // 0% from the left edge is as far left as we can go
  const leftIdealR =  width * 0.1;   // 10% from the left edge is ideal
  const leftMaxR =    width * 0.25;   // 20% from the left edge is as far right as we want to look
  const rightMinR =   width * 0.75;  // 75% from the left edge, start looking for right edge lines
  const rightIdealR = width * 0.9;   // 90% from the left edge is ideal
  const rightMaxR =   width * 1.25;   // 20% past the right edge

  // Compute and rank candidates
  type ScoredPeak = Peak & { score: number };
  const leftPeaks:  ScoredPeak[] = [];
  const rightPeaks: ScoredPeak[] = [];

  for (const peak of peaks) {
    let isLeftCandidate = false;
    let isRightCandidate = false;

    const theta = peak.thetaIndex * thetaStep;
    const r = peak.rIndex - rMax; // Convert back from index

    // Check if theta is within the vertical range
    const thetaDiff = Math.abs(Math.abs(theta - Math.PI / 2) - (Math.PI / 2));
    if (thetaDiff > verticalThreshold) continue;

    // Check if r is in the desired left range
    if (Math.abs(r) < leftMaxR) {
      isLeftCandidate = true;
    }
    if (Math.abs(r) > rightMinR && Math.abs(r) < rightMaxR) {
      isRightCandidate = true;
    }

    if (!isLeftCandidate && !isRightCandidate) continue;

    // Calculate similarity score
    let score = 0;

    // 1. Penalize based on distance from vertical
    score += Math.abs(thetaDiff) / 0.1;

    // 2. Penalize based on distance from the left edge range
    const percentFromIdeal =
      isLeftCandidate  ? Math.max(0, ((r - leftIdealR)  / (leftMaxR -  leftMinR)))  * 100 :
      isRightCandidate ? Math.max(0, ((r - leftIdealR)  / (leftMaxR -  leftMinR)))  * 100 : Infinity;
      //isRightCandidate ? Math.max(0, ((r - rightIdealR) / (rightMaxR - rightMinR))) * 100 : Infinity;

    score += 0.2 * percentFromIdeal;

    // 3. Penalize based on strength
    score += (maxScore - peak.votes) * 10;

    // Store the peak with its computed score
    if (isLeftCandidate)  leftPeaks.push({  ...peak, score });
    if (isRightCandidate) rightPeaks.push({ ...peak, score });
  }

  // Sort peaks by the lowest score and take the top 5
  leftPeaks.sort((a, b) => a.score - b.score);
  const leftLines = peaksToLines(leftPeaks.slice(0, 5));

  rightPeaks.sort((a, b) => a.score - b.score);
  const rightLines = peaksToLines(rightPeaks.slice(0, 5));
  

  // 5. Convert the top peaks into line endpoints.

  // Helper: compute line endpoints given (theta, r) for drawing.
  function getLineEndpoints(
    theta: number,
    r: number,
    width: number,
    height: number
  ): { x: number; y: number }[] {
    const points: { x: number; y: number }[] = [];

    // x = 0 => y = (r - x*cos(theta)) / sin(theta)
    if (Math.abs(Math.sin(theta)) > 1e-6) {
      const y0 = r / Math.sin(theta);
      if (y0 >= 0 && y0 <= height) {
        points.push({ x: 0, y: y0 });
      }
    }
    // x = width
    if (Math.abs(Math.sin(theta)) > 1e-6) {
      const y1 = (r - width * Math.cos(theta)) / Math.sin(theta);
      if (y1 >= 0 && y1 <= height) {
        points.push({ x: width, y: y1 });
      }
    }
    // y = 0 => x = (r - y*sin(theta)) / cos(theta)
    if (Math.abs(Math.cos(theta)) > 1e-6) {
      const x0 = r / Math.cos(theta);
      if (x0 >= 0 && x0 <= width) {
        points.push({ x: x0, y: 0 });
      }
    }
    // y = height
    if (Math.abs(Math.cos(theta)) > 1e-6) {
      const x1 = (r - height * Math.sin(theta)) / Math.cos(theta);
      if (x1 >= 0 && x1 <= width) {
        points.push({ x: x1, y: height });
      }
    }

    // Remove duplicate points (if any)
    const uniquePoints: { x: number; y: number }[] = [];
    for (const p of points) {
      if (!uniquePoints.some((q) => Math.abs(q.x - p.x) < 1e-6 && Math.abs(q.y - p.y) < 1e-6)) {
        uniquePoints.push(p);
      }
    }

    // If more than 2 valid intersections were found, pick the pair farthest apart.
    if (uniquePoints.length > 2) {
      let maxDistance = 0;
      let bestPair: { x: number; y: number }[] = [];
      for (let i = 0; i < uniquePoints.length; i++) {
        for (let j = i + 1; j < uniquePoints.length; j++) {
          const dx = uniquePoints[i].x - uniquePoints[j].x;
          const dy = uniquePoints[i].y - uniquePoints[j].y;
          const dist = dx * dx + dy * dy;
          if (dist > maxDistance) {
            maxDistance = dist;
            bestPair = [uniquePoints[i], uniquePoints[j]];
          }
        }
      }
      return bestPair;
    }
    return uniquePoints.slice(0, 2);
  }

  function peaksToLines(peaks: Peak[]) : Line[] {
    const lines: Line[] = [];
    for (const peak of peaks) {
      const theta = peak.thetaIndex * thetaStep;
      const r = peak.rIndex - rMax; // shift back to get the original r
      const endpoints = getLineEndpoints(theta, r, width, height);
      if (endpoints.length === 2) {
        lines.push({
          x1: endpoints[0].x,
          y1: endpoints[0].y,
          x2: endpoints[1].x,
          y2: endpoints[1].y,
          theta,
          r,
          unique: false,
        });
      }
    }
    return lines;
  }

  outputCtx.putImageData(originalImageData, 0, 0);

  const lines: Line[] = peaksToLines(topPeaks);
  const topEdgeLines: Line[] = peaksToLines(topEdgePoints);

  // Draw detected lines in red (top highest vote lines)
  outputCtx.strokeStyle = "red";
  outputCtx.lineWidth = 1;
  const uniqueLines: Line[] = [];
  for (const candidate of lines) {
    let isUnique = true;
    // Compare candidate with each already-accepted unique line
    for (const prevLine of uniqueLines) {
      const similarity =
        Math.abs(prevLine.theta - candidate.theta) * 20 +
        Math.abs(prevLine.r - candidate.r) / 10;
      if (similarity < 1) {
        isUnique = false;
        break;
      }
    }
    candidate.unique = isUnique;
      if (isUnique && drawAllUniqueLines) {
        outputCtx.strokeStyle = `rgba(${Math.max(110,255-uniqueLines.length*10)}, 110, 110, ${Math.max(0.5, 1-uniqueLines.length*0.05)})`;

        uniqueLines.push(candidate);
        outputCtx.beginPath();
        outputCtx.moveTo(candidate.x1, candidate.y1);
        outputCtx.lineTo(candidate.x2, candidate.y2);
        outputCtx.stroke();
      }
    if (uniqueLines.length >= 100) {
      break;
    }
  }
  console.log("uniqueLines.length", uniqueLines.length);
  console.log("uniqueLines, top 10: ", uniqueLines.slice(0, 10));

  // Build extra candidate lines from the top 200 peaks.
  const extraTopPeaks = peaks.slice(0, 2000);
  const extraCandidateLines: Line[] = [];
  for (const peak of extraTopPeaks) {
    const theta = peak.thetaIndex * thetaStep;
    const r = peak.rIndex - rMax; // shift back to get the original r value
    const endpoints = getLineEndpoints(theta, r, width, height);
    if (endpoints.length === 2) {
      extraCandidateLines.push({
        x1: endpoints[0].x,
        y1: endpoints[0].y,
        x2: endpoints[1].x,
        y2: endpoints[1].y,
        theta,
        r,
        unique: true,
      });
    }
  }

  // For horizontal lines, our target is theta = π/2.
  // For vertical lines, our target is theta = 0.
  // We'll compute a similarity value as:
  //    similarity = (|candidate.theta - targetTheta| * 20) + (|candidate_rPercent - expectedPercent|)
  // where candidate_rPercent is computed from candidate.r (as a percentage of height for horizontal,
  // and a percentage of width for vertical).

  const computeHorizontalSimilarity = (candidate: Line, expectedPercent: number): number => {
    const targetTheta = Math.PI / 2;
    const angleDiff = Math.abs(candidate.theta - targetTheta);
    // For horizontal lines (theta ~ π/2), r is essentially the y coordinate.
    const candidateRPercent = (candidate.r / height) * 100;
    const rDiff = Math.abs(candidateRPercent - expectedPercent);
    return angleDiff * 20 + rDiff;
  };

  const computeVerticalSimilarity = (candidate: Line, expectedPercent: number): number => {
    const targetTheta = 0;
    const angleDiff = Math.abs(candidate.theta - targetTheta);
    // For vertical lines (theta ~ 0), r is essentially the x coordinate.
    const candidateRPercent = (candidate.r / width) * 100;
    const rDiff = Math.abs(candidateRPercent - expectedPercent);
    return angleDiff * 20 + rDiff;
  };

  // We'll only consider candidates that are near the target orientation.
  const angleThreshold = 0.3; // radians

  // 1. Horizontal line about 5% down from the top (expected r% = 5)
  let bestTopCandidate: { candidate: Line; similarity: number } | null = null;
  for (const [index, candidate] of extraCandidateLines.entries()) {
    if (Math.abs(candidate.theta - Math.PI / 2) < angleThreshold) {
      const sim = computeHorizontalSimilarity(candidate, 5) + index;
      if (!bestTopCandidate || sim < bestTopCandidate.similarity) {
        //console.log("sim", sim);
        bestTopCandidate = { candidate, similarity: sim };
      }
    }
  }

  // 2. Horizontal line about 5% up from the bottom (expected r% = 95)
  let bestBottomCandidate: { candidate: Line; similarity: number } | null = null;
  for (const [index, candidate] of extraCandidateLines.entries()) {
    if (Math.abs(candidate.theta - Math.PI / 2) < angleThreshold) {
      const sim = computeHorizontalSimilarity(candidate, 95) + index;
      if (!bestBottomCandidate || sim < bestBottomCandidate.similarity) {
        bestBottomCandidate = { candidate, similarity: sim };
      }
    }
  };

  // 3. Vertical line about 5% from the left (expected r% = 5)
  let bestLeftCandidate: { candidate: Line; similarity: number } | null = null;
  for (const [index, candidate] of extraCandidateLines.entries()) {
    if (Math.abs(candidate.theta - 0) < angleThreshold) {
      const sim = computeVerticalSimilarity(candidate, 5) + index;
      if (!bestLeftCandidate || sim < bestLeftCandidate.similarity) {
        bestLeftCandidate = { candidate, similarity: sim };
      }
    }
  };

  // 4. Vertical line about 5% from the right (expected r% = 95)
  let bestRightCandidate: { candidate: Line; similarity: number } | null = null;
  for (const [index, candidate] of extraCandidateLines.entries()) {
    if (Math.abs(candidate.theta - 0) < angleThreshold) {
      const sim = computeVerticalSimilarity(candidate, 95) + index;
      if (!bestRightCandidate || sim < bestRightCandidate.similarity) {
        bestRightCandidate = { candidate, similarity: sim };
      }
    }
  };

  bestLeftCandidate = {candidate: leftLines[0], similarity: 0};
  bestRightCandidate = {candidate: rightLines[0], similarity: 0};

  const bestCandidates = {
    top: bestTopCandidate?.candidate,
    bottom: bestBottomCandidate?.candidate,
    left: bestLeftCandidate?.candidate,
    right: bestRightCandidate?.candidate,
  }
  console.log("bestCandidates", bestCandidates);

  if(drawEdgesAndCorners) {
    // Draw the found extra lines in blue.
    outputCtx.lineWidth = 4;
    outputCtx.strokeStyle = "blue";
    if (bestTopCandidate) {
      outputCtx.beginPath();
      outputCtx.moveTo(bestTopCandidate.candidate.x1, bestTopCandidate.candidate.y1);
      outputCtx.lineTo(bestTopCandidate.candidate.x2, bestTopCandidate.candidate.y2);
      outputCtx.stroke();
    } else {
      console.log("No top line found");
    }
    outputCtx.strokeStyle = "green";
    if (bestBottomCandidate) {
      outputCtx.beginPath();
      outputCtx.moveTo(bestBottomCandidate.candidate.x1, bestBottomCandidate.candidate.y1);
      outputCtx.lineTo(bestBottomCandidate.candidate.x2, bestBottomCandidate.candidate.y2);
      outputCtx.stroke();
    } else {
      console.log("No bottom line found");
    }
    outputCtx.strokeStyle = "yellow";
    if (bestLeftCandidate) {
      outputCtx.beginPath();
      outputCtx.moveTo(bestLeftCandidate.candidate.x1, bestLeftCandidate.candidate.y1);
      outputCtx.lineTo(bestLeftCandidate.candidate.x2, bestLeftCandidate.candidate.y2);
      outputCtx.stroke();
    } else {
      console.log("No left line found");
    }
    outputCtx.strokeStyle = "orange";
    if (bestRightCandidate) {
      outputCtx.beginPath();
      outputCtx.moveTo(bestRightCandidate.candidate.x1, bestRightCandidate.candidate.y1);
      outputCtx.lineTo(bestRightCandidate.candidate.x2, bestRightCandidate.candidate.y2);
      outputCtx.stroke();
    } else {
      console.log("No right line found");
    }
  }

  if (!bestCandidates.top || !bestCandidates.bottom || !bestCandidates.left || !bestCandidates.right) {
    console.log("Failed to find all edges");
    return;
  }

  const tightCorners = [
    findIntersection(bestCandidates.top, bestCandidates.left),
    findIntersection(bestCandidates.top, bestCandidates.right),
    findIntersection(bestCandidates.bottom, bestCandidates.left),
    findIntersection(bestCandidates.bottom, bestCandidates.right),
  ].filter((corner) => corner !== null) as { x: number; y: number }[];

  if (tightCorners.length < 4) {
    console.log("Failed to find all corners");
    return;
  }

  if(drawEdgesAndCorners) {
    tightCorners.forEach((corner) => {
      drawDot(outputCtx, corner.x, corner.y);
    });
  }

  const centerX = tightCorners.reduce((sum, corner) => sum + corner.x, 0) / tightCorners.length;
  const centerY = tightCorners.reduce((sum, corner) => sum + corner.y, 0) / tightCorners.length;

  const looseTop = offsetLine(bestCandidates.top, 10, {x: centerX, y: centerY});
  const looseBottom = offsetLine(bestCandidates.bottom, 10, {x: centerX, y: centerY});
  const looseLeft = offsetLine(bestCandidates.left, 10, {x: centerX, y: centerY});
  const looseRight = offsetLine(bestCandidates.right, 10, {x: centerX, y: centerY});

  const looseCorners = [
    findIntersection(looseTop, looseLeft),
    findIntersection(looseTop, looseRight),
    findIntersection(looseBottom, looseLeft),
    findIntersection(looseBottom, looseRight),
  ].filter((corner) => corner !== null) as { x: number; y: number }[];

  deskewCanvas(outputCanvas, looseCorners);
  //deskewCanvas(outputCanvas, tightCorners);

  // Mark canvas as contentful
  outputCanvas.setAttribute("contentful", "true");
}


export const deskewCanvas = (outputCanvas: HTMLCanvasElement, corners: {x: number, y: number}[]): void => {
  const outputCtx = outputCanvas?.getContext("2d");
  if (!outputCtx) {
    console.error("Failed to get output canvas context to deskew");
    return;
  }

  const srcMat = cv.imread(outputCanvas);

  const dsize = new cv.Size(outputCanvas.width, outputCanvas.height);
  const dstMat = new cv.Mat();

  const destinationPoints = [
    { x: 0, y: 0 },
    { x: outputCanvas.width, y: 0 },
    { x: 0, y: outputCanvas.height },
    { x: outputCanvas.width, y: outputCanvas.height },
  ];

  const HomographyMatrix = computeHomography(corners, destinationPoints);
  const Hdata: number[] = [
    HomographyMatrix[0], HomographyMatrix[1], HomographyMatrix[2],
    HomographyMatrix[3], HomographyMatrix[4], HomographyMatrix[5],
    HomographyMatrix[6], HomographyMatrix[7], 1.0  // force h33 = 1
  ];
  const homographyMat = cv.matFromArray(3, 3, cv.CV_64F, Hdata);


  cv.warpPerspective(
    srcMat,        // input image
    dstMat,        // output image
    homographyMat, // 3x3 homography matrix
    dsize,         // size of the destination image
    cv.INTER_LINEAR,
    cv.BORDER_CONSTANT,
    new cv.Scalar()
  );

  cv.imshow(outputCanvas, dstMat);

  // 7. Clean up memory.
  srcMat.delete();
  dstMat.delete();
  homographyMat.delete();
};

function skeletonize(
  edgeData: Uint8ClampedArray,
  thinEdgeData: Uint8ClampedArray,
  width: number,
  height: number,
  edgeThreshold: number
): void {
  // Convert edgeData to a binary image (0 or 1)
  const binaryImage: Uint8Array = new Uint8Array(width * height);
  
  for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          binaryImage[y * width + x] = edgeData[i] > edgeThreshold ? 1 : 0;
      }
  }

  let changed: boolean;
  do {
      changed = false;
      const toBeRemoved: [number, number][] = [];

      // First sub-iteration
      for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
              if (binaryImage[y * width + x] === 1) {
                  const p = getNeighborhood(binaryImage, x, y, width);
                  const transitions = countTransitions(p);
                  const neighbors = countNeighbors(p);

                  if (
                      neighbors >= 2 &&
                      neighbors <= 6 &&
                      transitions === 1 &&
                      p[1] * p[3] * p[5] === 0 &&
                      p[3] * p[5] * p[7] === 0
                  ) {
                      toBeRemoved.push([x, y]);
                      changed = true;
                  }
              }
          }
      }

      toBeRemoved.forEach(([x, y]) => {
          binaryImage[y * width + x] = 0;
      });

      toBeRemoved.length = 0;

      // Second sub-iteration
      for (let y = 1; y < height - 1; y++) {
          for (let x = 1; x < width - 1; x++) {
              if (binaryImage[y * width + x] === 1) {
                  const p = getNeighborhood(binaryImage, x, y, width);
                  const transitions = countTransitions(p);
                  const neighbors = countNeighbors(p);

                  if (
                      neighbors >= 2 &&
                      neighbors <= 6 &&
                      transitions === 1 &&
                      p[1] * p[3] * p[7] === 0 &&
                      p[1] * p[5] * p[7] === 0
                  ) {
                      toBeRemoved.push([x, y]);
                      changed = true;
                  }
              }
          }
      }

      toBeRemoved.forEach(([x, y]) => {
          binaryImage[y * width + x] = 0;
      });

  } while (changed);

  // Convert back to RGBA format
  for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
          const i = (y * width + x) * 4;
          const val = binaryImage[y * width + x] * 255;
          thinEdgeData[i] = thinEdgeData[i + 1] = thinEdgeData[i + 2] = val;
          thinEdgeData[i + 3] = 255;
      }
  }
}

function getNeighborhood(image: Uint8Array, x: number, y: number, width: number): number[] {
  return [
      image[(y - 1) * width + (x - 1)], image[(y - 1) * width + x], image[(y - 1) * width + (x + 1)],
      image[y * width + (x - 1)], image[y * width + x], image[y * width + (x + 1)],
      image[(y + 1) * width + (x - 1)], image[(y + 1) * width + x], image[(y + 1) * width + (x + 1)]
  ];
}

function countNeighbors(p: number[]): number {
  return p[0] + p[1] + p[2] + p[3] + p[5] + p[6] + p[7] + p[8];
}

function countTransitions(p: number[]): number {
  return (
      ((p[0] === 0 && p[1] === 1) ? 1 : 0) + ((p[1] === 0 && p[2] === 1) ? 1 : 0) +
      ((p[2] === 0 && p[5] === 1) ? 1 : 0) + ((p[5] === 0 && p[8] === 1) ? 1 : 0) +
      ((p[8] === 0 && p[7] === 1) ? 1 : 0) + ((p[7] === 0 && p[6] === 1) ? 1 : 0) +
      ((p[6] === 0 && p[3] === 1) ? 1 : 0) + ((p[3] === 0 && p[0] === 1) ? 1 : 0)
  );
}

// Function to find the intersection of two lines
function findIntersection(line1: any, line2: any) {
  const { x1: x1_1, y1: y1_1, x2: x2_1, y2: y2_1 } = line1;
  const { x1: x1_2, y1: y1_2, x2: x2_2, y2: y2_2 } = line2;

  const denominator = (x1_1 - x2_1) * (y1_2 - y2_2) - (y1_1 - y2_1) * (x1_2 - x2_2);
  
  if (denominator === 0) {
      console.error("Lines do not intersect (parallel)");
      return null;
  }

  const x = ((x1_1 * y2_1 - y1_1 * x2_1) * (x1_2 - x2_2) - (x1_1 - x2_1) * (x1_2 * y2_2 - y1_2 * x2_2)) / denominator;
  const y = ((x1_1 * y2_1 - y1_1 * x2_1) * (y1_2 - y2_2) - (y1_1 - y2_1) * (x1_2 * y2_2 - y1_2 * x2_2)) / denominator;

  return { x, y };
}

function drawDot(ctx: CanvasRenderingContext2D, x: number | null | undefined, y: number | null | undefined, color: string = "purple", size: number = 10) {
  if (x === null || y === null || x === undefined || y === undefined) { return; }
  ctx.beginPath();
  ctx.arc(x, y, size / 2, 0, 2 * Math.PI); // x, y, radius, startAngle, endAngle
  ctx.fillStyle = color;
  ctx.fill();
}

function computeHomography(src: { x: number; y: number }[], dst: { x: number; y: number }[]): mat3 {
  if (src.length !== 4 || dst.length !== 4) {
    throw new Error("Need exactly 4 source and 4 destination points.");
  }

  // Build the 8×8 system A * h = b.
  // For each correspondence i:
  //   Equation for u:  [ x, y, 1,  0, 0, 0,  -u*x, -u*y ] · h = u
  //   Equation for v:  [ 0, 0, 0,  x, y, 1,  -v*x, -v*y ] · h = v
  const A: number[][] = [];
  const b: number[] = [];

  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i];
    const { x: u, y: v } = dst[i];

    A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }

  // Solve the linear system A * h = b for h (8 unknowns).
  const h = solveLinearSystem(A, b); // See helper below

  // Form the 3×3 homography matrix; we set h33 = 1.
  const H = mat3.fromValues(
    h[0], h[1], h[2],
    h[3], h[4], h[5],
    h[6], h[7], 1
  );
  return H;
}

// A simple Gaussian elimination solver for small systems.
// (For more robust solutions, consider using a dedicated math library.)
function solveLinearSystem(A: number[][], b: number[]): number[] {
  const n = A.length;
  // Augment A with b to form an n×(n+1) matrix.
  for (let i = 0; i < n; i++) {
    A[i].push(b[i]);
  }

  // Forward elimination.
  for (let i = 0; i < n; i++) {
    // Find pivot.
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) {
        maxRow = k;
      }
    }
    // Swap rows.
    [A[i], A[maxRow]] = [A[maxRow], A[i]];

    // Normalize pivot row.
    const pivot = A[i][i];
    if (Math.abs(pivot) < 1e-10) {
      throw new Error("Matrix is singular or nearly singular");
    }
    for (let j = i; j < n + 1; j++) {
      A[i][j] /= pivot;
    }

    // Eliminate column i from other rows.
    for (let k = 0; k < n; k++) {
      if (k === i) continue;
      const factor = A[k][i];
      for (let j = i; j < n + 1; j++) {
        A[k][j] -= factor * A[i][j];
      }
    }
  }

  // Extract the solution.
  const x = new Array(n);
  for (let i = 0; i < n; i++) {
    x[i] = A[i][n];
  }
  return x;
}

function offsetLine(line: Line, offset: number, center: { x: number; y: number }): Line {
  const dx = line.x2 - line.x1;
  const dy = line.y2 - line.y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  // Normal vector (perpendicular)
  let nx = -dy / length;
  let ny = dx / length;

  // Determine if the center lies in the direction of the normal
  const vx = center.x - line.x1;
  const vy = center.y - line.y1;
  const dot = vx * nx + vy * ny;

  // If the center is in the direction of the normal, flip it (we want the opposite direction)
  if (dot > 0) {
    nx = -nx;
    ny = -ny;
  }

  // Offset both endpoints by the fixed amount along the normal
  return {
    x1: line.x1 + nx * offset,
    y1: line.y1 + ny * offset,
    x2: line.x2 + nx * offset,
    y2: line.y2 + ny * offset,
    theta: line.theta,
    r: line.r - offset, // if you need to update the Hough parameter, adjust accordingly
    unique: line.unique,
  };
}
